import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  FileVideo,
  LayoutDashboard,
  LineChart,
  MessageSquare,
  Settings,
  ShoppingCart,
  Store,
  Users,
  Wallet,
} from 'lucide-react';
import { cn } from '@mlm/ui';
import { getSiteManifest } from '@mlm/shared';
import { getTenantFeatures } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';

/** Sidebar routes this build's tenant manifest declares unused (its bespoke
 * site never renders the surfaces they curate). Cosmetic; routes still work. */
const MANIFEST_HIDDEN_NAV = new Set(
  getSiteManifest(import.meta.env.VITE_TENANT as string | undefined)?.adminHide?.sidebarItems ??
    [],
);

/**
 * Phase 12.5 admin shell — left rail with expandable sections.
 *
 * Each top-level item has a default `to` (where clicking the row navigates)
 * plus an optional `children` array of sub-routes that render below the
 * parent when the section is expanded. Sections auto-expand when any of
 * their routes is active, and the open/closed state for non-active
 * sections persists in localStorage.
 *
 * Per-leaf view permissions hide sub-items the user can't access. A
 * parent with no visible children is hidden too. super_admin bypasses
 * all checks. Permissions are baked into the JWT at login — changes
 * apply on the user's next refresh.
 */

type SubItem = { label: string; to: string; perm?: string; superAdmin?: boolean };

type SectionItem = {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  /** Routes that should mark this section active (exact OR prefix match). */
  match: string[];
  children?: SubItem[];
  /** Perm required for flat (no-children) items. */
  perm?: string;
  /** Hard super-admin gate — hidden from everyone but super_admin. */
  superAdmin?: boolean;
};

const ITEMS: SectionItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, match: ['/'] },
  {
    label: 'Members',
    to: '/members',
    icon: Users,
    match: ['/members', '/kyc-review', '/slots', '/memberships', '/access'],
    children: [
      { label: 'Users', to: '/members', perm: 'view.members.users' },
      { label: 'KYC Review', to: '/kyc-review', perm: 'view.members.users' },
      { label: 'Slots', to: '/slots', perm: 'view.members.slots' },
      { label: 'Memberships', to: '/memberships', perm: 'view.members.memberships' },
      { label: 'Access', to: '/access', perm: 'view.members.access' },
    ],
  },
  {
    label: 'Commerce',
    to: '/orders',
    icon: ShoppingCart,
    match: [
      '/orders',
      '/products',
      '/virtual-products',
      '/categories',
      '/variation-types',
      '/suppliers',
      '/purchase-orders',
      '/coupons',
      '/flash-sale',
      '/landing-packages',
      '/featured-packages',
      '/branches',
      '/shipping',
      '/merchants',
      '/ecom-payment-methods',
      '/pos-payment-methods',
    ],
    children: [
      { label: 'Orders', to: '/orders', perm: 'order.read' },
      { label: 'Products', to: '/products', perm: 'view.products.catalog' },
      // Services / digital goods — same permission, separate catalog. The two
      // lists partition the products table, so nothing shows up in both.
      // Dropped entirely unless the tenant enables virtual products (below).
      { label: 'Virtual Products', to: '/virtual-products', perm: 'view.products.catalog' },
      { label: 'Categories', to: '/categories', perm: 'view.products.catalog' },
      { label: 'Variation Types', to: '/variation-types', perm: 'view.products.catalog' },
      { label: 'Suppliers', to: '/suppliers', perm: 'view.products.suppliers' },
      { label: 'Purchase Orders', to: '/purchase-orders', perm: 'view.products.purchase_orders' },
      { label: 'Coupons', to: '/coupons', perm: 'view.products.coupons' },
      { label: 'Flash Sale', to: '/flash-sale', perm: 'view.settings.tenant' },
      { label: 'Landing Packages', to: '/landing-packages', perm: 'view.settings.tenant' },
      { label: 'Featured Packages', to: '/featured-packages', perm: 'view.settings.tenant' },
      { label: 'Branches', to: '/branches', perm: 'view.branch.branches' },
      { label: 'Shipping', to: '/shipping', perm: 'view.branch.shipping' },
      { label: 'Payment methods', to: '/ecom-payment-methods', perm: 'view.settings.tenant' },
      { label: 'POS payment methods', to: '/pos-payment-methods', perm: 'view.settings.tenant' },
      { label: 'Merchants', to: '/merchants', perm: 'view.merchants.accounts' },
    ],
  },
  {
    label: 'Messages',
    to: '/messages',
    icon: MessageSquare,
    match: ['/messages'],
    perm: 'view.messages',
  },
  {
    label: 'Feedback',
    to: '/feedback',
    icon: MessageSquare,
    match: ['/feedback'],
    perm: 'view.feedback',
  },
  {
    label: 'Finance',
    to: '/payments',
    icon: Wallet,
    match: ['/payments', '/wallet-topups', '/payouts', '/payout-fees'],
    children: [
      { label: 'Payments', to: '/payments', perm: 'view.payments' },
      { label: 'Wallet Top-Ups', to: '/wallet-topups', perm: 'topup.review' },
      { label: 'Payouts', to: '/payouts', perm: 'view.payouts.requests' },
      { label: 'Fees & Tax', to: '/payout-fees', perm: 'view.payouts.fees' },
    ],
  },
  {
    label: 'Compensation',
    to: '/compplan',
    icon: BarChart3,
    match: [
      '/compplan',
      '/periods',
      '/distribute',
      '/distribution-runs',
      '/distributions',
      '/vortex',
    ],
    children: [
      { label: 'Plans', to: '/compplan', perm: 'view.compensation.compplan' },
      { label: 'Periods', to: '/periods', perm: 'view.compensation.periods' },
      { label: 'Unilevel', to: '/distribute', perm: 'view.compensation.distribute' },
      { label: 'Distribution Runs', to: '/distribution-runs', perm: 'view.compensation.distribute' },
      { label: 'Distributions', to: '/distributions', perm: 'view.compensation.distributions' },
      { label: 'Vortex Monitor', to: '/vortex', perm: 'view.compensation.vortex' },
    ],
  },
  {
    label: 'Storefront',
    to: '/storefront',
    icon: Store,
    match: ['/storefront', '/layout-theme', '/official-feedback'],
    children: [
      { label: 'Content', to: '/storefront', perm: 'view.settings.tenant' },
      { label: 'Layout & Theme', to: '/layout-theme', superAdmin: true },
      { label: 'Official Store Feedback', to: '/official-feedback', perm: 'view.products.catalog' },
    ],
  },
  {
    label: 'Content',
    to: '/training-videos',
    icon: FileVideo,
    match: ['/training-videos', '/replicated-page', '/announcements', '/special-links'],
    children: [
      { label: 'Training Videos', to: '/training-videos', perm: 'view.assets.training_videos' },
      // Testimonials moved into the product editor (per-product "Testimonials" tab) —
      // they're per-product data, not a global CMS surface.
      { label: 'Replicated Pages', to: '/replicated-page', perm: 'view.content.replicated' },
      { label: 'Announcements', to: '/announcements', perm: 'view.announcements' },
      { label: 'Special Links', to: '/special-links', perm: 'view.assets.training_videos' },
    ],
  },
  {
    label: 'Reports',
    to: '/reports',
    icon: LineChart,
    match: ['/reports'],
    perm: 'view.reports',
    children: [
      { label: 'Overview', to: '/reports', perm: 'view.reports' },
      { label: 'Stockist Receivables', to: '/reports/stockist-receivables', perm: 'view.reports' },
    ],
  },
  {
    label: 'Settings',
    to: '/settings',
    icon: Settings,
    match: ['/settings', '/audit', '/dlq', '/data-import'],
    children: [
      { label: 'System', to: '/settings', perm: 'view.settings.tenant' },
      { label: 'Audit log', to: '/audit', perm: 'view.settings.audit' },
      // Failed-job recovery — a diagnostics surface alongside the audit log, not
      // a payouts concern. Perm code stays `view.payouts.dlq` (renaming a seeded
      // permission is a separate DB change); only the nav home moved.
      { label: 'Failed jobs (DLQ)', to: '/dlq', perm: 'view.payouts.dlq' },
      // Super-admin only: bulk import from a legacy Firestore .xlsx export.
      { label: 'Legacy Import', to: '/data-import', superAdmin: true },
    ],
  },
];

/**
 * Accounting preview section — injected after Finance ONLY when the tenant's
 * `accounting_enabled` feature flag is on (Settings → System toggle). Pages
 * render sample data until the accounting module ships; no per-leaf perms yet
 * (the real build adds `view.accounting`).
 */
const ACCOUNTING_ITEM: SectionItem = {
  label: 'Accounting',
  to: '/accounting',
  icon: BookOpen,
  match: ['/accounting'],
  children: [
    { label: 'Overview', to: '/accounting' },
    { label: 'Chart of Accounts', to: '/accounting/accounts' },
    { label: 'Journal', to: '/accounting/journal' },
    { label: 'Statements', to: '/accounting/statements' },
    { label: 'Supplier Invoices', to: '/accounting/ap' },
    { label: 'Costing & Margins', to: '/accounting/costing' },
    { label: 'Billing Statement', to: '/accounting/billing' },
  ],
};

const EXPAND_KEY = 'mlm.admin.sidebar.expanded';

function isSectionActive(item: SectionItem, pathname: string): boolean {
  return item.match.some((m) =>
    m === '/' ? pathname === '/' : pathname === m || pathname.startsWith(`${m}/`),
  );
}

function isChildActive(child: SubItem, pathname: string): boolean {
  return pathname === child.to || pathname.startsWith(`${child.to}/`);
}

function loadExpanded(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(EXPAND_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpanded);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = !!user?.roles.includes('super_admin');

  // Same cache entry the Settings page uses, so flipping the System toggle
  // updates the rail immediately. A staff account whose role can't read
  // tenant features just doesn't get the section (error → hidden).
  const featuresQuery = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
    staleTime: 60_000,
    retry: false,
  });
  const accountingEnabled = featuresQuery.data?.accounting_enabled === true;
  // Master switch for the virtual-products feature. A tenant selling only
  // physical goods never sees the entry. Same cache entry as Settings, so the
  // rail updates the moment the toggle is saved.
  const virtualProductsEnabled = featuresQuery.data?.virtual_products_enabled === true;

  function canSee(perm: string | undefined): boolean {
    if (!perm) return true;
    if (isSuperAdmin) return true;
    return !!user?.perms.includes(perm);
  }

  // Combined gate: a `superAdmin` item is hidden from everyone but super_admin
  // (a hard role gate that granting a perm can't open); otherwise perm-based.
  function canSeeItem(it: { perm?: string; superAdmin?: boolean }): boolean {
    if (it.superAdmin && !isSuperAdmin) return false;
    return canSee(it.perm);
  }

  // Accounting slots in right after Finance when the tenant flag is on.
  const allItems: SectionItem[] = accountingEnabled
    ? ITEMS.flatMap((item) => (item.label === 'Finance' ? [item, ACCOUNTING_ITEM] : [item]))
    : ITEMS;

  // Filter items down to what the user is allowed to see. Drop sub-items the
  // user has no perm for. A section with children loses entirely if all its
  // children are gone — the parent without any child has nothing to lead to.
  const visibleItems: SectionItem[] = allItems.flatMap((item) => {
    if (item.children) {
      const visibleChildren = item.children.filter(
        (c) =>
          canSeeItem(c) &&
          !MANIFEST_HIDDEN_NAV.has(c.to) &&
          (c.to !== '/virtual-products' || virtualProductsEnabled),
      );
      if (visibleChildren.length === 0) return [];
      // Re-point `to` to the first VISIBLE child so clicking the parent
      // never lands on a forbidden page.
      return [{ ...item, to: visibleChildren[0]!.to, children: visibleChildren }];
    }
    return canSeeItem(item) ? [item] : [];
  });

  // Auto-expand the active section so the user always sees where they are.
  useEffect(() => {
    const active = visibleItems.find((i) => i.children && isSectionActive(i, pathname));
    if (active && !expanded[active.label]) {
      setExpanded((prev) => ({ ...prev, [active.label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    window.localStorage.setItem(EXPAND_KEY, JSON.stringify(expanded));
  }, [expanded]);

  function toggleSection(label: string) {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  return (
    <aside
      className={cn(
        'h-screen sticky top-0 border-r bg-card flex flex-col transition-[width] duration-150',
        collapsed ? 'w-[64px]' : 'w-[240px]',
      )}
    >
      <div
        className={cn(
          'flex items-center h-14 border-b px-4',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {!collapsed ? (
          <span className="font-semibold text-sm tracking-tight">MLM.Admin</span>
        ) : null}
        <button
          type="button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="text-muted-foreground hover:text-foreground transition-colors"
          onClick={onToggleCollapsed}
        >
          <ChevronLeft
            className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')}
          />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {visibleItems.map((item) => {
            const sectionActive = isSectionActive(item, pathname);
            const hasChildren = !!item.children?.length;
            const isOpen = !collapsed && hasChildren && (sectionActive || expanded[item.label]);
            return (
              <li key={item.label}>
                <div className={cn('flex items-stretch')}>
                  <NavLink
                    to={item.to}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors flex-1',
                      collapsed && 'justify-center px-0',
                      sectionActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    {!collapsed ? <span className="flex-1">{item.label}</span> : null}
                  </NavLink>
                  {hasChildren && !collapsed ? (
                    <button
                      type="button"
                      aria-label={`Toggle ${item.label}`}
                      onClick={() => toggleSection(item.label)}
                      className={cn(
                        'px-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
                      )}
                    >
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform',
                          !isOpen && '-rotate-90',
                        )}
                      />
                    </button>
                  ) : null}
                </div>
                {hasChildren && isOpen ? (
                  <ul className="mt-0.5 ml-7 mb-1 border-l border-border pl-3 space-y-0.5">
                    {item.children!.map((child) => {
                      const childActive = isChildActive(child, pathname);
                      return (
                        <li key={child.to}>
                          <NavLink
                            to={child.to}
                            className={cn(
                              'block rounded-md px-2 py-1.5 text-xs transition-colors',
                              childActive
                                ? 'text-primary font-medium'
                                : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
